// UI-only row action for the mail queue: retry of a failed/bounced letter.
// Rendered ONLY for those two statuses (plan §7.3) — a resent `delivered`
// letter would double-mail the recipient.
import type { ReactElement } from 'react';
import { Button, Popconfirm, Tooltip, message } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { ApiError } from '../../../shared/api-client/index.js';
import type { AdminOutboxListItem } from '../api/admin.client.js';

type Translate = (key: string) => string;

/** Maps known admin API error codes to localized messages. */
function retryErrorMessage(err: unknown, t: Translate): string {
  if (err instanceof ApiError && err.code === 'invalid_status') {
    return t('crm.admin.mail.retry_invalid_status');
  }
  return t('crm.admin.errors.action_failed');
}

export interface AdminOutboxRetryActionProps {
  message: AdminOutboxListItem;
  pending: boolean;
  onRetry: (messageId: string) => Promise<unknown>;
}

export function AdminOutboxRetryAction({
  message: letter,
  pending,
  onRetry,
}: AdminOutboxRetryActionProps): ReactElement | null {
  const { t } = useTranslation();

  if (letter.status !== 'failed' && letter.status !== 'bounced') {
    return null;
  }

  async function runRetry(): Promise<void> {
    try {
      await onRetry(letter.id);
      void message.success(t('crm.admin.mail.retry_queued'));
    } catch (rejection: unknown) {
      void message.error(retryErrorMessage(rejection, t));
    }
  }

  const label = t('crm.admin.mail.retry');

  return (
    <Popconfirm
      title={label}
      description={t('crm.admin.mail.retry_confirm')}
      okText={label}
      cancelText={t('crm.common.cancel')}
      okButtonProps={{ loading: pending }}
      onConfirm={runRetry}
    >
      <Tooltip title={label}>
        <Button
          size="small"
          type="text"
          disabled={pending}
          aria-label={label}
          icon={<RedoOutlined />}
        />
      </Tooltip>
    </Popconfirm>
  );
}
